import React, { useRef, useEffect, useMemo, forwardRef } from "react";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { selectFreeSpinsActive } from "../../../State/selectors";
import { selectHoldAndWinActive } from "../../../State/Slices/holdAndWinSlice";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
  getCounterScale,
} from "../../../Contexts/SlotRegistryContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import FreeSpinsGameViewController, {
  FREE_SPINS_GAME_VIEW_SLOTS,
} from "./FreeSpinsGameViewController";
import FreeSpinBasicReelSet from "../../../Games/FreeSpinBasic/FreeSpinBasicReelSet/FreeSpinBasicReelSet";
import FreeSpinBasicOverlayReelSet from "../../../Games/FreeSpinBasic/FreeSpinBasicOverlayReelSet/FreeSpinBasicOverlayReelSet";
import DimReelSet from "../../../Games/Shared/DimReelSet/DimReelSet";
import WinBoxContainer from "../../../Games/Shared/WinBoxReelSet/WinBoxContainer";
import WinText from "../WinText/WinText";
import { WIN_TEXT_CONFIG } from "../WinText/WinTextController";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Fine-tuning offsets for the injected containers, in spine-local pixels.
// Both containers stack on the same grid, so they must move together.
const REELSET_X_OFFSET = 0;
const REELSET_Y_OFFSET = 0;

/**
 * FreeSpinsGameView — the Cleopatra's Fire free spins room.
 *
 * The simple twin of GameViewSimple: render the free_spins_game_view spine
 * (layout skin + idle) and inject the free spins board and its overlay into
 * the spine's two reel slots. Nothing else — no level boxes, no ladder, no
 * trails. The Mad Labs room that did all that (BonusGameView) is kept on disk
 * but no longer mounted.
 *
 * It READS state and never writes it. Which room is on screen is
 * `freeSpins.active`; GameViewSimple reads the same flag with the opposite
 * sign, so the two can never both be shown or both be hidden.
 *
 * Hidden by ALPHA, never by `visible` — see the note in GameViewSimple.
 *
 * The injected containers are counter-scaled: they are children of the spine,
 * so they would otherwise inherit its scale on top of their own, and the reels
 * already size themselves from symbolWidth/symbolHeight.
 *
 * The controller is published on `freeSpinsGameViewControllerRef` so the free
 * spins win controller can reach the scene.
 */
const FreeSpinsGameView = forwardRef((props, ref) => {
  const controllerRef = useRef(null);
  // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
  useJackpotPlates(controllerRef);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);

  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    dividerDimensions,
    reelSpacing,
    device,
    orientation,
    layout,
    isMobileSmall,
  } = useDimensions();

  const slotRegistry = useSlotRegistry();
  const {
    freeSpinsGameViewControllerRef,
    freeSpinBasicDimReelSetRef,
    freeSpinBasicWinBoxContainerRef,
    freeSpinBasicGlobalLineWinTextRef,
    freeSpinBasicLineWinTextRef,
    freeSpinsCoinTrailRef,
  } = useReelRefs();

  // ── The coins' fire trails into the logo (GDD §5.1) ──
  // A layer above the whole room — the board and the logo live in the scene
  // spine at zIndex 1, and the overlay slot content at 2 — the base room's
  // golden trail layer, copied. Published with the live symbol height, which
  // sizes the fire (Games/Shared/coinToLogoTrail).
  const coinTrailContainerRef = useRef(null);
  useEffect(() => {
    if (!freeSpinsCoinTrailRef) return undefined;
    freeSpinsCoinTrailRef.current = {
      trailContainer: coinTrailContainerRef.current,
      symbolHeight,
    };
    return () => {
      freeSpinsCoinTrailRef.current = null;
    };
  }, [freeSpinsCoinTrailRef, symbolHeight]);

  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  // H&W won inside free spins covers this room; FS stays active underneath.
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const spineCounterScale = useMemo(
    () => getCounterScale(symbolScale),
    [symbolScale],
  );

  // ─── Controller ───
  useEffect(() => {
    if (!containerRef.current || controllerRef.current) return;

    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;

    controllerRef.current = new FreeSpinsGameViewController(
      containerRef.current,
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
      slotRegistry,
    );

    if (freeSpinsGameViewControllerRef) {
      freeSpinsGameViewControllerRef.current = controllerRef.current;
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
        if (freeSpinsGameViewControllerRef) {
          freeSpinsGameViewControllerRef.current = null;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Inject the reel container into the spine's reel slot ───
  const reelSlot = useSlot(FREE_SPINS_GAME_VIEW_SLOTS.REEL_SET);
  useEffect(() => {
    if (!reelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    reelSlot.addChild(node);
    return () => {
      if (node.parent === reelSlot) reelSlot.removeChild(node);
    };
  }, [reelSlot]);

  // ─── Inject the overlay container into the spine's overlay slot ───
  const overlaySlot = useSlot(FREE_SPINS_GAME_VIEW_SLOTS.OVERLAY_REEL_SET);
  useEffect(() => {
    if (!overlaySlot || !overlayContainerRef.current) return;
    const node = overlayContainerRef.current;
    overlaySlot.addChild(node);
    return () => {
      if (node.parent === overlaySlot) overlaySlot.removeChild(node);
    };
  }, [overlaySlot]);

  // ─── Undo the spine's inherited scale on both injected containers ───
  useEffect(() => {
    if (reelContainerRef.current) {
      reelContainerRef.current.scale.set(spineCounterScale);
    }
    if (overlayContainerRef.current) {
      overlayContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  // ─── Resize ───
  useEffect(() => {
    if (!controllerRef.current) return;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
    );
  }, [
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  ]);

  return (
    <Container
      ref={containerRef}
      name="free_spins_game_view"
      sortableChildren
      // The SAME layer as game_view — the two rooms never share the screen.
      zIndex={0}
      alpha={freeSpinsActive && !holdAndWinActive ? 1 : 0.001}
    >
      {/* Reels — injected into the spine's reel slot, clipped to the window */}
      <Container
        ref={reelContainerRef}
        name="free_spins_reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        sortableChildren
        eventMode="passive"
      >
        {/* The board exists only while the room is live. `active` flips at
            the wipe's midpoint, in both directions, so it is built and
            destroyed under full cover. The two containers themselves stay:
            they are injected into the spine's slots. */}
        {freeSpinsActive && (
          <>
            <FreeSpinBasicReelSet
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              dividerDimensions={dividerDimensions}
              reelSpacing={reelSpacing}
            />

            {/* Oversized on purpose, as in GameViewSimple: this container is
                clipped to the frame window by `reels_window_clip`, so the dim
                covers the window exactly, edge to edge. */}
            <DimReelSet
              ref={freeSpinBasicDimReelSetRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              dividerDimensions={dividerDimensions}
              paddingMultiplier={0.1}
            />
          </>
        )}
      </Container>

      {/* Overlay — injected into the spine's overlay slot, above the frame */}
      <Container
        ref={overlayContainerRef}
        name="free_spins_overlay_reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        zIndex={2}
        sortableChildren
        eventMode="passive"
      >
        {freeSpinsActive && (
          <>
            <FreeSpinBasicOverlayReelSet
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              reelSpacing={reelSpacing}
            />

            {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
              <Container name="fs_win_text_overlay" zIndex={6}>
                <WinText
                  ref={freeSpinBasicGlobalLineWinTextRef}
                  fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
                  hideOnBigWin={true}
                />
                <WinText
                  ref={freeSpinBasicLineWinTextRef}
                  fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
                  hideOnBigWin={true}
                />
              </Container>
            )}

            {FEATURES_CONFIG.WIN_BOX.ENABLED && (
              <WinBoxContainer
                ref={freeSpinBasicWinBoxContainerRef}
                symbolWidth={symbolWidth}
                symbolHeight={symbolHeight}
                reelSpacing={reelSpacing}
              />
            )}
          </>
        )}
      </Container>

      {/* The coins' fire trails into the logo — see coinTrailContainerRef. */}
      <Container
        ref={coinTrailContainerRef}
        name="free_spins_coin_trail_container"
        zIndex={3}
        sortableChildren
        eventMode="passive"
      />
    </Container>
  );
});

FreeSpinsGameView.displayName = "FreeSpinsGameView";

export default FreeSpinsGameView;
